import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Play, BookOpen, Star, Wind } from 'lucide-react';
import { format } from 'date-fns';
import { articleHtml, sanitize } from '@/lib/articleBody';
import { hasGuidedTimer, headingMatches, sectionId } from '@/lib/breathing';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND}${p}`);

const BASE_CATS = ['All', "Coach's Corner", 'Nutrition', 'Training', 'Recovery', 'Mindset', 'Longevity'];
const OPTIONAL_CATS = ['Supplements', 'Breathing'];
const CAT_COLOR = {
  "Coach's Corner": '#FF6FA8', Nutrition: '#65A30D', Training: '#00C8FF',
  Recovery: '#7DD3FC', Mindset: '#FFB800', Longevity: '#A78BFA',
  Supplements: '#F97316', Breathing: '#2DD4BF',
};

function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function Academy() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState('All');
  const [active, setActive] = useState(null);
  const [breathing, setBreathing] = useState([]);

  useEffect(() => {
    api.get('/articles').then(r => setArticles(r.data));
    api.get('/recovery/activities').then(r => setBreathing(r.data.filter(hasGuidedTimer))).catch(() => {});
  }, []);
  const linkedId = params.get('article');
  useEffect(() => {
    if (!linkedId) setActive(null);
    else if (articles.length) setActive(articles.find(a => a.id === linkedId) || null);
  }, [linkedId, articles]);
  const open = (a) => setParams({ article: a.id });
  const close = () => { if (loc.state?.from) nav(-1); else { setParams({}); setActive(null); } };

  const CATS = [...BASE_CATS, ...OPTIONAL_CATS.filter(c => articles.some(a => a.category === c))];
  const filtered = cat === 'All' ? articles : articles.filter(a => a.category === cat);
  const featured = articles.find(a => a.featured) || articles.find(a => a.category === "Coach's Corner");

  if (active) return <ArticleDetail article={active} section={params.get('section')} activities={breathing.filter(b => b.breathing.academy_article_id === active.id)}
    onStart={(b) => nav(`/app/breathing/${b.id}`, { state: { from: 'academy' } })} onBack={close} />;

  return (
    <div className="space-y-5 pb-8" data-testid="client-academy">
      <div>
        <div className="label-mini text-[#FF6FA8]">Learn</div>
        <h1 className="font-display font-black text-3xl mt-1">YourReGen Academy</h1>
        <p className="text-sm text-muted-foreground mt-1">Learn the YourReGen Protocol across all pillars.</p>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-none">
        {CATS.map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`academy-cat-${c.replace(/\W/g,'').toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${cat === c ? 'bg-white text-[#081726]' : 'bg-[var(--c-film)] text-foreground/80'}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Featured Coach's Corner */}
      {cat === 'All' && featured && (
        <button onClick={() => open(featured)} data-testid="academy-featured" className="w-full text-left regen-card-elevated overflow-hidden active:scale-[0.99] transition">
          <div className="relative h-40">
            {imgSrc(featured.image_url) && <img src={imgSrc(featured.image_url)} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-bg)] via-[var(--c-bg)]/40 to-transparent" />
            <div className="absolute top-3 left-3 pill bg-[#FF6FA8] text-[#081726] text-[10px] font-bold flex items-center gap-1"><Star className="w-3 h-3" /> {featured.category}</div>
            <div className="absolute bottom-3 left-4 right-4">
              <div className="font-display font-black text-lg leading-tight">{featured.title}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{featured.excerpt}</div>
            </div>
          </div>
        </button>
      )}

      {/* Article list */}
      {filtered.length === 0 ? (
        <div className="regen-card-elevated p-8 text-center text-muted-foreground text-sm">
          <BookOpen className="w-7 h-7 mx-auto mb-2 opacity-50" />
          No articles in this category yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.filter(a => cat !== 'All' || !a.featured).map(a => {
            const color = CAT_COLOR[a.category] || '#00C8FF';
            return (
              <button key={a.id} onClick={() => open(a)} data-testid={`academy-article-${a.id}`} className="w-full text-left regen-card-elevated overflow-hidden flex active:scale-[0.99] transition">
                <div className="w-24 h-24 flex-shrink-0 relative bg-[var(--c-film)]">
                  {imgSrc(a.image_url) && <img src={imgSrc(a.image_url)} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />}
                  {ytId(a.youtube_url) && <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-6 h-6 text-[var(--c-ink)]" /></div>}
                </div>
                <div className="flex-1 min-w-0 p-3">
                  <div className="label-mini" style={{ color }}>{a.category}</div>
                  <div className="font-display font-bold text-sm mt-0.5 line-clamp-2">{a.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{a.excerpt}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArticleDetail({ article, section, activities, onStart, onBack }) {
  const color = CAT_COLOR[article.category] || '#00C8FF';
  const vid = ytId(article.youtube_url);
  const chunks = useMemo(() => buildChunks(sanitize(articleHtml(article)), activities), [article, activities]);
  useEffect(() => {
    if (!section) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => document.getElementById(sectionId(section))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [section, chunks]);
  return (
    <div className="space-y-4 pb-8" data-testid="academy-detail">
      <button onClick={onBack} data-testid="academy-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ChevronLeft className="w-4 h-4" /> Back to YourReGen Academy
      </button>

      {imgSrc(article.image_url) && (
        <div className="rounded-2xl overflow-hidden h-48 md:h-auto bg-[var(--c-film)]" data-testid="academy-article-banner">
          <img src={imgSrc(article.image_url)} alt={article.title} className="w-full h-full object-cover md:h-auto md:max-h-[560px] md:object-contain md:mx-auto" />
        </div>
      )}

      <div>
        <div className="label-mini" style={{ color }}>{article.category}</div>
        <h1 className="font-display font-black text-2xl mt-1 leading-tight">{article.title}</h1>
        <div className="text-xs text-muted-foreground mt-1">{article.author} · {article.created_at ? format(new Date(article.created_at), 'MMM d, yyyy') : ''}</div>
      </div>

      {vid && (
        <div className="rounded-2xl overflow-hidden aspect-video">
          <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${vid}`} title={article.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      )}

      <div className="space-y-4" data-testid="academy-article-body">
        {chunks.map((c, i) => c.act
          ? <button key={i} onClick={() => onStart(c.act)} data-testid={`academy-start-breathing-${c.act.id}`}
              className="w-full flex items-center justify-center gap-2 min-h-12 py-3 px-5 text-center rounded-full bg-[#7DD3FC] text-[#081726] font-semibold active:scale-[0.98] transition-transform">
              <Wind className="w-4 h-4 flex-shrink-0" /> Start Guided Breathing · {c.act.name}
            </button>
          : <div key={i} className="rt-content text-foreground/90" dangerouslySetInnerHTML={{ __html: c.html }} />)}
      </div>
    </div>
  );
}

// Splits the article at its headings and places each linked activity's button at the end of its section.
function buildChunks(html, activities) {
  const root = document.createElement('div');
  root.innerHTML = html;
  const chunks = []; let buf = ''; let pending = null; const placed = new Set();
  const flush = () => { if (buf) { chunks.push({ html: buf }); buf = ''; } };
  const place = () => { if (pending) { flush(); chunks.push({ act: pending.act }); placed.add(pending.act.id); pending = null; } };
  root.childNodes.forEach(node => {
    const m = /^H([1-6])$/.exec(node.nodeName || '');
    if (node.nodeName === 'HR') place();
    if (m) {
      const level = +m[1];
      if (pending && level <= pending.level) place();
      const act = activities.find(a => !placed.has(a.id) && pending?.act.id !== a.id && headingMatches(node.textContent, a.breathing.academy_section));
      if (act) { node.id = sectionId(act.breathing.academy_section); pending = { act, level }; }
    }
    buf += node.nodeType === 1 ? node.outerHTML : (node.textContent || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  });
  place(); flush();
  activities.filter(a => !placed.has(a.id)).forEach(act => chunks.push({ act }));
  return chunks;
}
